import { useState, useMemo } from "react";
import { num, toCm, ml, money, ceilStep, minSale, soportes, DESP_OPTS, ROLLOS, FACTORES, REDONDEOS, sv, svPrice, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, ResultCard, CutDiagram } from "./shared";

export default function Cortinas({ onSave, s }) {
  const [u, setU] = useState("m");
  const [ancho, setAncho] = useState("2");
  const [alto, setAlto] = useState("2.50");
  const [instal, setInstal] = useState("Techo a piso");
  const [tipo, setTipo] = useState("Tela decorativa");
  const [apertura, setApertura] = useState("Central");
  const [riel, setRiel] = useState("Riel recto");
  const [rollo, setRollo] = useState("140");
  const [factor, setFactor] = useState("2.00");
  const [patron, setPatron] = useState("Sin patrón");
  const [rapport, setRapport] = useState("0");
  const [orient, setOrient] = useState("Libre");
  const [motor, setMotor] = useState("No");
  const [cantidad, setCantidad] = useState("1");
  const [desp, setDesp] = useState(sv(s, "desperdicio_default"));
  const [ventaMin, setVentaMin] = useState("1");
  const [redondeo, setRedondeo] = useState("0");
  const [mSup, setMSup] = useState("15");
  const [mInf, setMInf] = useState("15");
  const [dobLat, setDobLat] = useState("5");
  const [carrosCada, setCarrosCada] = useState(sv(s, "carros_cada_cm"));
  const [ojillosCada, setOjillosCada] = useState(sv(s, "ojillos_cada_cm"));
  const [precioTela, setPrecioTela] = useState(svPrice(s, "precio_tela_m"));
  const [precioConf, setPrecioConf] = useState(svPrice(s, "precio_confeccion"));
  const [precioInst, setPrecioInst] = useState(svPrice(s, "precio_instalacion"));
  const [precioAcc, setPrecioAcc] = useState("");

  const r = useMemo(() => {
    const cant = Math.max(1, Math.round(num(cantidad)) || 1);
    const anchoCm = toCm(ancho, u), altoCm = toCm(alto, u), rolloCm = num(rollo) || 140;
    const anchoConf = anchoCm * num(factor);
    const anchoUtil = Math.max(1, rolloCm - 2 * num(dobLat));
    const panos = anchoConf > 0 ? Math.ceil(anchoConf / anchoUtil) : 0;
    let largoCorte = altoCm + num(mSup) + num(mInf);
    const rap = num(rapport);
    const patronAplica = patron !== "Sin patrón" && rap > 0;
    if (patronAplica) largoCorte = ceilStep(largoCorte, rap);
    const telaCm = panos * largoCorte * (1 + num(desp) / 100);
    const telaM = (telaCm / 100) * cant;
    const ventaM = ceilStep(minSale(telaM, num(ventaMin)), num(redondeo));
    const aprov = panos > 0 ? (anchoConf / (panos * anchoUtil)) * 100 : 0;
    const rielM = (anchoCm / 100) * cant;
    const sop = soportes(anchoCm / 100) * cant;
    const esBarra = riel === "Barra decorativa";
    const carros = !esBarra ? Math.ceil(anchoConf / Math.max(1, num(carrosCada))) * cant : 0;
    const argollas = esBarra ? Math.ceil(anchoConf / Math.max(1, num(carrosCada))) * cant : 0;
    const ojillos = tipo === "Con ojillos" ? Math.ceil(anchoConf / Math.max(1, num(ojillosCada))) * cant : 0;
    const cinta = tipo === "Con cinta fruncidora" ? (anchoConf / 100) * cant : 0;
    const esMotor = motor === "Sí" || riel === "Riel motorizado";
    const cMat = ventaM * num(precioTela) + num(precioAcc);
    const cConf = num(precioConf) * cant;
    const cInst = num(precioInst);
    const total = cMat + cConf + cInst;
    const w = [];
    if (num(factor) < 1.8) w.push("El factor de fruncido es bajo; la cortina puede verse plana.");
    if (panos > 6) w.push(`Número de paños elevado (${panos}); revisa el ancho del rollo.`);
    if (largoCorte > 600) w.push("La altura supera la longitud máxima recomendada del rollo.");
    if (patronAplica) w.push("El patrón (rapport) incrementó el largo de cada paño para no cortar el diseño.");
    if (orient === "No puede girarse") w.push("El estampado no permite girar las piezas: no se optimiza por rotación.");
    if (anchoConf > rolloCm && panos <= 1) w.push("El ancho del rollo puede ser incompatible con el ancho confeccionado.");
    const rows = [
      { section: "Cortina" },
      { label: "Tipo de cortina", value: tipo }, { label: "Tipo de instalación", value: instal },
      { label: "Apertura", value: apertura }, { label: "Riel/Barra", value: riel },
      { label: "Ancho del espacio", value: `${(anchoCm / 100).toFixed(2)} m` }, { label: "Alto del espacio", value: `${(altoCm / 100).toFixed(2)} m` },
      { label: "Cantidad de cortinas", value: `${cant}` },
      { section: "Tela" },
      { label: "Factor de fruncido", value: num(factor).toFixed(2) },
      { label: "Ancho confeccionado", value: ml(anchoConf / 100) },
      { label: "Número de paños", value: `${panos}` },
      { label: "Largo de cada paño", value: ml(largoCorte / 100) },
      { label: "Desperdicio aplicado", value: `${num(desp)}%` },
      { label: "Aprovechamiento de tela", value: `${aprov.toFixed(0)}%` },
      { label: "Metros lineales requeridos", value: ml(telaM) },
      { label: "Metros lineales a vender", value: ml(ventaM), strong: true },
      { section: "Accesorios" },
      { label: "Longitud de riel/barra", value: ml(rielM) },
      { label: "Soportes", value: `${sop}` },
      ...(carros ? [{ label: "Carros / ganchos", value: `${carros}` }] : []),
      ...(argollas ? [{ label: "Argollas", value: `${argollas}` }] : []),
      ...(ojillos ? [{ label: "Ojillos", value: `${ojillos}` }] : []),
      ...(cinta ? [{ label: "Cinta fruncidora", value: ml(cinta) }] : []),
      ...(esMotor ? [{ label: "Motorización", value: "Motor + control + transformador + receptor" }] : []),
      { section: "Costos" },
      { label: "Materiales (tela + accesorios)", value: money(cMat) },
      { label: "Confección", value: money(cConf) },
      { label: "Instalación", value: money(cInst) },
      { label: "Total estimado", value: money(total), strong: true },
    ];
    const pieces = panos > 0 ? [{ w: anchoUtil, h: largoCorte, count: panos }] : [];
    const inputs = { u, ancho, alto, tipo, riel, rollo, factor, cantidad };
    return { rows, warnings: w, rollCm: rolloCm, pieces, total, inputs };
  }, [u, ancho, alto, instal, tipo, apertura, riel, rollo, factor, patron, rapport, orient, motor, cantidad, desp, ventaMin, redondeo, mSup, mInf, dobLat, carrosCada, ojillosCada, precioTela, precioConf, precioInst, precioAcc]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo medir para una cortina?">
          <p><b>Ancho:</b> de extremo a extremo; ideal sobresalir 15–30 cm por lado. <b>Alto:</b> según instalación (techo a piso, 20/10 cm arriba de la ventana, alféizar, etc.).</p>
        </HowTo>
        <Card>
          <Sel label="Unidad de medida" value={u} onChange={setU} opts={[["mm", "Milímetros"], ["cm", "Centímetros"], ["m", "Metros"]]} testid="cort-unidad" />
          <Sel label="Tipo de cortina" value={tipo} onChange={setTipo} opts={["Tela decorativa", "Blackout", "Visillo", "Doble cortina", "Onda perfecta", "Pliegue americano", "Pliegue francés", "Ripple Fold", "Con ojillos", "Con cinta fruncidora"]} testid="cort-tipo" />
          <Field label="Ancho del espacio" value={ancho} onChange={setAncho} suffix={u} testid="cort-ancho" />
          <Field label="Alto del espacio" value={alto} onChange={setAlto} suffix={u} testid="cort-alto" />
          <Sel label="Tipo de instalación" value={instal} onChange={setInstal} opts={["Techo a piso", "20 cm arriba → piso", "10 cm arriba → piso", "Hasta el alféizar", "20 cm debajo", "Altura personalizada"]} testid="cort-instal" />
          <Sel label="Apertura" value={apertura} onChange={setApertura} opts={["Central", "Lateral izquierda", "Lateral derecha", "Dos hojas"]} testid="cort-apertura" />
          <Sel label="Tipo de riel" value={riel} onChange={setRiel} opts={["Riel recto", "Riel curvo", "Barra decorativa", "Riel motorizado"]} testid="cort-riel" />
          <Sel label="Ancho del rollo de tela (cm)" value={rollo} onChange={setRollo} opts={ROLLOS.map((x) => [x, `${x} cm`])} testid="cort-rollo" />
          <Sel label="Factor de fruncido" value={factor} onChange={setFactor} opts={FACTORES} testid="cort-factor" />
          <Sel label="Patrón (rapport)" value={patron} onChange={setPatron} opts={["Sin patrón", "Patrón vertical", "Patrón horizontal"]} testid="cort-patron" />
          {patron !== "Sin patrón" && <Field label="Rapport (cm)" value={rapport} onChange={setRapport} step="1" suffix="cm" testid="cort-rapport" />}
          <Sel label="Orientación del estampado" value={orient} onChange={setOrient} opts={["Libre", "Obligatoria", "No puede girarse"]} testid="cort-orient" />
          <Sel label="Motorización" value={motor} onChange={setMotor} opts={["No", "Sí"]} testid="cort-motor" />
          <Field label="Cantidad de cortinas iguales" value={cantidad} onChange={setCantidad} step="1" min="1" testid="cort-cantidad" />
          <Sel label="Desperdicio" value={desp} onChange={setDesp} opts={DESP_OPTS} testid="cort-desp" />
          <Sel label="Venta mínima" value={ventaMin} onChange={setVentaMin} opts={[["0.25", "0.25 m"], ["0.50", "0.50 m"], ["1", "1.00 m"]]} testid="cort-ventamin" />
          <Sel label="Redondeo comercial" value={redondeo} onChange={setRedondeo} opts={REDONDEOS} testid="cort-redondeo" />
        </Card>
        <Advanced>
          <Field label="Margen superior (cm)" value={mSup} onChange={setMSup} step="1" suffix="cm" testid="cort-msup" />
          <Field label="Margen inferior (cm)" value={mInf} onChange={setMInf} step="1" suffix="cm" testid="cort-minf" />
          <Field label="Dobladillo/costura lateral (cm)" value={dobLat} onChange={setDobLat} step="1" suffix="cm" testid="cort-doblat" />
          <Field label="Carros/ganchos cada (cm)" value={carrosCada} onChange={setCarrosCada} step="1" suffix="cm" testid="cort-carros" />
          <Field label="Ojillos cada (cm)" value={ojillosCada} onChange={setOjillosCada} step="1" suffix="cm" testid="cort-ojillos" />
          <Field label="Precio tela ($/m)" value={precioTela} onChange={setPrecioTela} suffix="$" testid="cort-precio-tela" />
          <Field label="Confección ($/cortina)" value={precioConf} onChange={setPrecioConf} suffix="$" testid="cort-precio-conf" />
          <Field label="Instalación ($)" value={precioInst} onChange={setPrecioInst} suffix="$" testid="cort-precio-inst" />
          <Field label="Accesorios ($)" value={precioAcc} onChange={setPrecioAcc} suffix="$" testid="cort-precio-acc" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Cortina — GOÛT" modality="cortinas" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave}
        diagram={<CutDiagram rollCm={r.rollCm} pieces={r.pieces} />} />
    </CalcLayout>
  );
}
